/**
 * Modelos de la integración con Odoo (contrato: kredit-api-MT/docs/odoo/ESPEC.md).
 * Todo tipado; sin `any`.
 */

export const PERMISO_ODOO_LEER = 'config.integracion_odoo.read';
export const PERMISO_ODOO_ESCRIBIR = 'config.integracion_odoo.write';

export type OdooTipoMaestro = 'cliente' | 'comercio';
export type OdooEstadoMaestro = 'pendiente' | 'anotado' | 'error';
export type OdooErrorTipo = 'datos' | 'transitorio';
export type OdooClaseArchivo = 'creditos' | 'pagos';
export type OdooEstadoArchivo = 'subido' | 'error' | 'anotado' | 'con_problemas';
export type OdooEstadoIngesta = 'recibido' | 'en_espera' | 'anotado' | 'con_problemas';
export type OdooEstadoConexion = 'ok' | 'sin_configurar' | 'error';
export type OdooBuzonTipo = 'local' | 'drive';
export type OdooAccionBitacora = 'alta' | 'modificacion' | 'consulta' | 'token' | 'archivo' | 'revision';
export type OdooOrigen = 'aprobacion' | 'tiempo-real' | 'manual' | 'cron';

/** Envoltorio común de la API del repo. */
export interface OdooRespuesta<T> {
  ok: boolean;
  data: T;
  msg?: string;
}

export interface OdooMaestro {
  _id?: string;
  tipo: OdooTipoMaestro;
  source_id: string;
  nombre: string;
  identificacion: string;
  estado: OdooEstadoMaestro;
  error_tipo: OdooErrorTipo | null;
  ultimo_error: string | null;
  intentos: number;
  odoo_id: string | null;
  fe_alta: string | null;
  fe_ultimo_envio: string | null;
  fe_ultima_modif: string | null;
}

export interface OdooBitacoraItem {
  _id: string;
  tipo: string;
  source_id: string;
  accion: OdooAccionBitacora;
  origen: OdooOrigen;
  ok: boolean;
  status: number | null;
  duracion_ms: number | null;
  peticion: unknown;
  respuesta: unknown;
  error: string | null;
  usuario: string | null;
  createdAt: string;
}

export interface OdooIngestaProblema {
  fila: number;
  motivo: string;
}

export interface OdooIngesta {
  nombre: string;
  estado: OdooEstadoIngesta;
  filas: number;
  anotadas: number;
  problemas: OdooIngestaProblema[];
}

export interface OdooArchivo {
  _id: string;
  fecha_operacion: string;
  clase: OdooClaseArchivo;
  nombre: string;
  filas: number;
  monto_total_usd: number;
  buzon_tipo: OdooBuzonTipo;
  ubicacion: string;
  estado: OdooEstadoArchivo;
  error: string | null;
  origen: 'cron' | 'manual';
  createdAt: string;
  estado_simulador?: OdooIngesta | null;
}

export interface OdooConexion {
  estado: OdooEstadoConexion;
  mensaje: string;
  probado_en: string | null;
}

export interface OdooConteos {
  anotados: number;
  pendientes: number;
  errores: number;
}

export interface OdooConfigResumen {
  enabled: boolean;
  crear_activo: boolean;
  modificar_activo: boolean;
  archivos_activo: boolean;
  hora_subida: number;
  buzon_tipo: OdooBuzonTipo;
}

/** Una clase de archivo dentro de un día (giros = «creditos», o pagos). */
export interface OdooArchivoClaseDia {
  filas: number;
  estado: OdooEstadoArchivo;
  error: string | null;
}

/** Los dos archivos de un día (YYYY-MM-DD); null si ese día no hubo de esa clase. */
export interface OdooArchivoDia {
  fecha: string;
  creditos: OdooArchivoClaseDia | null;
  pagos: OdooArchivoClaseDia | null;
}

export interface OdooProblemaFrecuente {
  motivo: string;
  n: number;
}

export interface OdooResumen {
  conexion: OdooConexion;
  maestros: OdooConteos & { por_tipo: Record<OdooTipoMaestro, OdooConteos> };
  problema_frecuente?: OdooProblemaFrecuente | null;
  archivos_por_dia?: OdooArchivoDia[];
  ultimos_archivos: OdooArchivo[];
  proxima_subida: string | null;
  config_resumen: OdooConfigResumen;
}

export interface OdooPagina<T> {
  items: T[];
  total: number;
  page?: number;
  limit?: number;
}

export interface OdooMaestrosFiltro {
  tipo: OdooTipoMaestro | '';
  estado: OdooEstadoMaestro | '';
  q: string;
  page: number;
  limit: number;
}

export interface OdooMaestroDetalle {
  maestro: OdooMaestro;
  bitacora: OdooBitacoraItem[];
}

export interface OdooBitacoraFiltro {
  tipo: string;
  ok: '' | 'true' | 'false';
  page: number;
  limit: number;
}

export interface OdooConfig extends OdooConfigResumen {
  api_url: string;
  api_login: string;
  tiene_clave: boolean;
  drive_folder_id: string;
  ultima_subida: string | null;
  updated_by: string | null;
}

/** Cambio parcial: solo viajan los campos que se tocaron. */
export type OdooConfigCambio = Partial<Omit<OdooConfig, 'tiene_clave' | 'ultima_subida' | 'updated_by'>> & {
  api_password?: string;
};

export interface OdooPrueba {
  estado: OdooEstadoConexion;
  mensaje: string;
  ms: number;
}

/** Estado de una carga para pintar con async pipe. */
export interface OdooCarga<T> {
  cargando: boolean;
  error: string | null;
  datos: T | null;
}
